import React, { useState, useMemo } from 'react';
import { Table, SpinningLoader, Button, Chip } from '@frontend/design-system';
import { useListRegistrationsQuery } from '../queries/sales-demo.queries';
import type { Registration } from '../types/sales-demo.types';

const PAGE_SIZE = 25;

const statusChipVariant: Record<string, 'success' | 'critical' | 'neutral'> = {
  HEALTHY: 'success',
  UNHEALTHY: 'critical',
  DRAINING: 'neutral',
};

function heartbeatToMs(ht: string | { seconds?: number } | undefined): number {
  if (!ht) return 0;
  if (typeof ht === 'string') {
    const t = new Date(ht).getTime();
    return Number.isNaN(t) ? 0 : t;
  }
  if (ht.seconds) return ht.seconds * 1000;
  return 0;
}

function formatHeartbeat(ms: number): string {
  if (!ms) return 'Never';
  const seconds = Math.floor((Date.now() - ms) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m ago`;
}

type RegistryRow = {
  id: string;
  address: string;
  host: string;
  port: string;
  status: string;
  type: string;
  ephemeralId: string;
  datasetNames: string[];
  heartbeatMs: number;
};

function toRow(reg: Registration): RegistryRow {
  const address = reg.address || 'N/A';
  const [host, port] = address.split(':');
  const specs = (reg as any).datasetSpecs ?? (reg as any).dataset_specs ?? [];
  const ephemeralId = (reg as any).ephemeralId ?? (reg as any).ephemeral_id ?? '';
  const heartbeat = (reg as any).heartbeatTime ?? (reg as any).heartbeat_time;
  return {
    id: reg.id || address,
    address,
    host: host || 'N/A',
    port: port || '',
    status: reg.status || 'UNKNOWN',
    type: ephemeralId ? 'Ephemeral' : 'Permanent',
    ephemeralId,
    datasetNames: specs.map((s: any) => (s.typeUrl ?? s.type_url ?? '').split('/').pop() || 'unknown'),
    heartbeatMs: heartbeatToMs(heartbeat),
  };
}

const colConfig = [
  {
    id: 'seeder',
    Header: 'Seeder',
    accessor: (row: RegistryRow) => row.address,
    cellRenderer: (_: string, row?: RegistryRow) => (
      <div>
        <div style={{ fontWeight: 600, fontSize: '13px', color: '#1a1a1a', fontFamily: 'monospace' }}>{row?.host}</div>
        {row?.port && (
          <div style={{ fontSize: '11px', color: '#6F7780', fontFamily: 'monospace' }}>Port {row.port}</div>
        )}
      </div>
    ),
  },
  {
    id: 'status',
    Header: 'Status',
    accessor: (row: RegistryRow) => row.status,
    cellRenderer: (status: string) => (
      <Chip variant={statusChipVariant[status] ?? 'neutral'} size='tiny'>
        {status}
      </Chip>
    ),
  },
  {
    id: 'type',
    Header: 'Type',
    accessor: (row: RegistryRow) => row.type,
    cellRenderer: (type: string, row?: RegistryRow) =>
      type === 'Ephemeral' ? (
        <Chip variant='primary' size='tiny'>
          {row?.ephemeralId || 'Ephemeral'}
        </Chip>
      ) : (
        <span style={{ fontSize: '12px', color: '#6F7780' }}>Permanent</span>
      ),
  },
  {
    id: 'datasets',
    Header: 'Datasets',
    disableSortBy: true,
    accessor: (row: RegistryRow) => row,
    cellRenderer: (row: RegistryRow) => (
      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' as const }}>
        {row.datasetNames.length > 0 ? (
          row.datasetNames.map((name, i) => (
            <Chip key={i} variant='neutral' size='tiny'>
              {name}
            </Chip>
          ))
        ) : (
          <span style={{ fontSize: '12px', color: '#8E959E' }}>None</span>
        )}
      </div>
    ),
  },
  {
    id: 'heartbeat',
    Header: 'Last Heartbeat',
    accessor: (row: RegistryRow) => row.heartbeatMs,
    cellRenderer: (ms: number) => <span style={{ fontSize: '12px', color: '#6F7780' }}>{formatHeartbeat(ms)}</span>,
  },
];

export const RegistryList: React.FC = () => {
  const [pageToken, setPageToken] = useState('');
  const [pageHistory, setPageHistory] = useState<string[]>(['']);

  const {
    data: registrationsData,
    isLoading,
    isFetching,
    refetch,
  } = useListRegistrationsQuery({
    pageSize: PAGE_SIZE,
    pageToken,
    filter: '',
  });

  const registrations = registrationsData?.registrations ?? [];
  const nextPageToken = (registrationsData as any)?.nextPageToken ?? (registrationsData as any)?.next_page_token ?? '';
  const currentPageIndex = pageHistory.indexOf(pageToken);
  const hasNextPage = !!nextPageToken;
  const hasPrevPage = currentPageIndex > 0;

  const tableData = useMemo(() => registrations.map(toRow), [registrations]);

  const goNextPage = () => {
    if (!nextPageToken) return;
    setPageHistory((prev) => {
      const next = [...prev];
      if (!next.includes(nextPageToken)) next.push(nextPageToken);
      return next;
    });
    setPageToken(nextPageToken);
  };

  const goPrevPage = () => {
    if (currentPageIndex <= 0) return;
    setPageToken(pageHistory[currentPageIndex - 1]);
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <SpinningLoader />
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 0',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '12px', color: '#6F7780' }}>
            Page {currentPageIndex + 1} &middot; {registrations.length} seeder{registrations.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Button variant='secondary' size='small' iconName='arrow-left' onClick={goPrevPage} disabled={!hasPrevPage}>
            Prev
          </Button>
          <Button variant='secondary' size='small' iconName='arrow-right' onClick={goNextPage} disabled={!hasNextPage}>
            Next
          </Button>
          <div style={{ height: '20px', width: '1px', backgroundColor: '#A1A8B0' }} />
          <Button variant='secondary' size='small' iconName='refresh' onClick={() => refetch()}>
            Refresh
          </Button>
        </div>
      </div>

      <div
        style={{
          borderRadius: '8px',
          border: `1px solid #A1A8B0`,
          overflow: 'hidden',
          opacity: isFetching ? 0.6 : 1,
          transition: 'opacity 0.15s',
        }}
      >
        {registrations.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px', color: '#6F7780' }}>
            <div style={{ fontSize: '14px' }}>No seeders found</div>
          </div>
        ) : (
          <Table colConfig={colConfig} data={tableData} />
        )}
      </div>
    </div>
  );
};
